import moment from "moment";
import SingleSelectComponent from "../../components/single-select/single-select.component";
import { transStatus } from "../../config/global-variables";
import modelBaseV2 from "../../models/model-base-v2.model";
import IncentiveReportModel from "../../models/incentive-report/incentive-report.model";
import ViewBase from "../view-base.view";

/**
 * Incentive Report
 *
 * FFIS payout reporting for a period: summary by position, one row per person, store roll-up,
 * and a per-person component trail. Menu WM021105, APPROVER.
 *
 * Mirrors the Validation Fail Over 10% page - filters, a grid, an export, and a drill-down -
 * per the implementation spec §6.2.
 *
 * ============================================================================================
 * THE DRILL-DOWN IS THE POINT OF THE PAGE
 * ============================================================================================
 * The spec calls it "the feature that replaces asking the analyst", and that is exactly right.
 * A total nobody can decompose is a number people argue about. Each component carries its
 * inputs, the rate and the multiplier applied, the tier it landed in, and parameterTrace - a
 * derivation string the primitives write as they calculate. Render the trace; it is the answer
 * to "why is my figure this" without re-running anything.
 *
 * ============================================================================================
 * ⚠️ SHOW runAuthority. AN APPROVER CAN OTHERWISE SIGN OFF ON THE WRONG NUMBERS.
 * ============================================================================================
 * Since BATCH_NIGHTLY became exempt from the FR-03 gate, the unattended job runs every night and
 * its inputs were never reviewed by anyone. The server now prefers a payout run when resolving
 * the scope and only falls back to a nightly one, but the fallback is silent in the data - the
 * rows look identical either way.
 *
 * So runAuthority (GATED / PROVISIONAL / SIMULATION) is rendered as a banner, not a column. An
 * approver reading PROVISIONAL figures is looking at last night's snapshot rather than the payout
 * they are about to approve, and the two can legitimately differ.
 *
 * @author FFIS
 */
export default function IncentiveReportView() {
  ViewBase.call(this);

  const PRODUCT_GROUPS = [
    { key: 'MX', value: 'MX' },
    { key: 'AV', value: 'AV' },
  ];

  const PERIOD_TYPES = [
    { key: 'MONTHLY', value: 'MONTHLY' },
    { key: 'WEEKLY', value: 'WEEKLY' },
  ];

  const PAID_FILTERS = [
    { key: '', value: 'Everyone in scope' },
    { key: 'true', value: 'Paid only (non-zero)' },
  ];

  const TABS = {
    summary: 'SUMMARY',
    employee: 'EMPLOYEE',
    store: 'STORE',
  };

  const DEFAULT_SCOPE = {
    productGroupCode: 'MX',
    periodType: 'MONTHLY',
    periodCode: '',
    calcRunId: '',
  };

  let scope = structuredClone(DEFAULT_SCOPE);
  let positionCode = '';
  let siteCode = '';
  let paidOnly = '';

  let activeTab = TABS.summary;

  let summary = null;
  let employees = [];
  let stores = [];

  let breakdownUserId = null;
  let breakdown = null;

  let exportCaveat = '';

  const readEnvelope = (result) => (Array.isArray(result) ? result[0] : result?.transResult?.[0]);

  const scopeIsComplete = () => !!(scope.productGroupCode && scope.periodType && scope.periodCode);

  /** Only send calcRunId when the user pinned one; blank means "let the server resolve it". */
  const scopeParams = () => ({
    productGroupCode: scope.productGroupCode,
    periodType: scope.periodType,
    periodCode: scope.periodCode,
    calcRunId: scope.calcRunId ? Number(scope.calcRunId) : undefined,
  });

  /* ---- loading ----------------------------------------------------------- */

  /**
   * A refusal from these procedures arrives as a ONE-ROW ENVELOPE, not as an empty list - the
   * "no run exists for this scope" case. Rendered as a grid it would look like a single blank
   * record, so detect it and say what it is instead.
   */
  const isRefusal = (rows) => Array.isArray(rows) && rows.length === 1
    && rows[0] && rows[0].transStatus && rows[0].message;

  const loadSummary = () => {
    this.showLoading();
    IncentiveReportModel.getSummary(scopeParams())
      .then((result) => {
        const header = result?.[0] || result?.header || [];
        if (isRefusal(header)) {
          summary = { refusal: header[0].message };
          return;
        }
        summary = {
          header: header[0] || null,
          positions: result?.[1] || result?.positions || [],
          zeroReasons: result?.[2] || result?.zeroReasons || [],
        };
      })
      .catch((error) => { this.handleError(error); })
      .finally(() => { this.hideLoading(); });
  };

  const loadEmployees = () => {
    this.showLoading();
    IncentiveReportModel.getEmployees({
      ...scopeParams(),
      positionCode: positionCode || undefined,
      siteCode: siteCode || undefined,
      paidOnly: paidOnly === 'true' ? true : undefined,
    })
      .then((result) => {
        employees = isRefusal(result) ? [] : (result || []);
        if (isRefusal(result)) {
          this.showMessage(result[0].message, transStatus.failed);
        }
      })
      .catch((error) => { this.handleError(error); })
      .finally(() => { this.hideLoading(); });
  };

  const loadStores = () => {
    this.showLoading();
    IncentiveReportModel.getStores(scopeParams())
      .then((result) => {
        stores = isRefusal(result) ? [] : (result || []);
        if (isRefusal(result)) {
          this.showMessage(result[0].message, transStatus.failed);
        }
      })
      .catch((error) => { this.handleError(error); })
      .finally(() => { this.hideLoading(); });
  };

  const loadBreakdown = (userId) => {
    this.showLoading();
    IncentiveReportModel.getEmployeeBreakdown(userId, scopeParams())
      .then((result) => {
        breakdown = {
          employee: result?.[0]?.[0] || result?.employee?.[0] || null,
          components: result?.[1] || result?.components || [],
        };
        breakdownUserId = userId;
      })
      .catch((error) => { this.handleError(error); })
      .finally(() => { this.hideLoading(); });
  };

  const loadActiveTab = () => {
    if (!scopeIsComplete()) {
      return;
    }
    loadSummary();
    if (activeTab === TABS.employee) { loadEmployees(); }
    if (activeTab === TABS.store) { loadStores(); }
  };

  /* ---- export ------------------------------------------------------------ */

  /**
   * The export routes return JSON rows, not a file, so the workbook is assembled here.
   *
   * ⚠️ CSV, NOT .xlsx. This project has no xlsx library, and the approval template's real shape
   * is a multi-sheet workbook (Summary, EC, SPV, Store Achievement). A CSV of the '1. EC' column
   * contract is what can be produced honestly today; calling it an approval workbook would be a
   * lie the approval process eventually discovers. The filename says CSV so nobody is surprised.
   */
  const toCsv = (rows) => {
    if (!rows.length) {
      return '';
    }
    const cols = Object.keys(rows[0]);
    const cell = (v) => {
      if (v === null || v === undefined) { return ''; }
      const s = String(v);
      /* Quote anything that would otherwise break the column structure. A reason text with a
         comma in it is the common case. */
      return /[",\r\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
    };
    return [cols.join(',')]
      .concat(rows.map(r => cols.map(c => cell(r[c])).join(',')))
      .join('\r\n');
  };

  const onExport = (exportType) => {
    const warn = exportType === 'PAYROLL'
      ? this.getText('EM00000288',
          'A PAYROLL export is refused unless this run has an APPROVED approval - money must '
        + 'not leave on nobody\'s authority. Continue?')
      : this.getText('EM00000289',
          'Export the APPROVAL sheet for this scope?');

    this.showConfirm(warn, this.getText('LT00000022', 'Save'), (e) => {
      if (e !== 'YES') {
        return;
      }
      this.showLoading();
      IncentiveReportModel.exportRows(exportType, scopeParams())
        .then((result) => {
          /* A refusal comes back as a message, never as an export with no rows - an empty file
             is something somebody ships. */
          const rows = result?.rows || result?.[0] || [];
          exportCaveat = result?.caveat || '';

          if (isRefusal(rows) || !rows.length) {
            const msg = (Array.isArray(rows) && rows[0] && rows[0].message)
              || result?.message
              || this.getText('EM00000290',
                   'Nothing was exported. This is a refusal, not an empty period - check the '
                 + 'message from the server.');
            this.showMessage(msg, transStatus.failed);
            return;
          }

          const name = 'FFIS_' + exportType + '_' + scope.periodCode + '_'
            + moment().format('YYYYMMDDHHmmss') + '.csv';

          /* ⚠️ THE '\ufeff' IS A BYTE-ORDER MARK AND IS LOAD-BEARING - do not delete it as
             stray whitespace. Excel opens a UTF-8 CSV as the local codepage unless the file
             starts with a BOM, which turns Indonesian, Thai and Vietnamese names into mojibake.
             The same class of mistake the fact tables were fixed for: a VARCHAR copy of a
             Unicode source silently degrades the data rather than failing. */
          modelBaseV2.downloadBlob(
            new Blob(['\ufeff' + toCsv(rows)], { type: 'text/csv;charset=utf-8;' }), name);

          if (exportCaveat) {
            this.showMessage(exportCaveat, transStatus.success);
          }
        })
        .catch((error) => { this.handleError(error); })
        .finally(() => { this.hideLoading(); });
    });
  };

  /* ---- render helpers ---------------------------------------------------- */

  const num = (v, dp) => (v === null || v === undefined
    ? '-'
    : Number(v).toLocaleString(undefined, {
        minimumFractionDigits: dp || 0, maximumFractionDigits: dp || 0 }));

  /** Blank rather than 0 where a value is genuinely absent - see the store note in the model. */
  const nullable = (v, dp) => (v === null || v === undefined
    ? m('span.text-muted', '-') : num(v, dp));

  const pct = (v) => (v === null || v === undefined ? '-' : (Number(v) * 100).toFixed(1) + '%');

  const authorityBanner = () => {
    const h = summary && summary.header;
    if (!h) {
      return null;
    }
    const a = h.runAuthority || '';
    if (a.indexOf('PROVISIONAL') === 0) {
      return m('.alert.alert-warning', [
        m('span.fw-semibold', this.getText('LT00000338', 'PROVISIONAL FIGURES. ')),
        this.getText('EM00000291',
          'This is a nightly batch run. It is exempt from the FR-03 sanity check, so nobody '
        + 'reviewed the target and sellout behind these numbers, and it CANNOT be approved. '
        + 'Run a payout calculation before signing anything off.'),
      ]);
    }
    if (a.indexOf('SIMULATION') === 0) {
      return m('.alert.alert-secondary', [
        m('span.fw-semibold', this.getText('LT00000339', 'SIMULATION. ')),
        this.getText('EM00000292', 'A what-if. No entitlement, and not approvable.'),
      ]);
    }
    return m('.alert.alert-success.py-2', [
      m('span.fw-semibold', this.getText('LT00000340', 'GATED. ')),
      this.getText('EM00000293',
        'The FR-03 sanity check was confirmed for this scope before these figures were '
      + 'calculated.'),
    ]);
  };

  const runLine = () => {
    const h = summary && summary.header;
    if (!h) {
      return null;
    }
    return m('.small.text-muted.mb-2',
      this.getText('LT00000290', 'Run') + ' ' + h.calcRunId
      + ' | ' + (h.runType || '') + ' | ' + (h.statusCode || '')
      + ' | ' + (h.schemeCode || '') + ' ' + (h.schemeVersion || '')
      + ' | ' + num(h.rowsSucceeded) + ' ' + this.getText('LT00000294', 'employees')
      + (h.runSelection ? ' | ' + h.runSelection : ''));
  };

  /* ---- sections ---------------------------------------------------------- */

  const filterSection = () => m('.card.mb-3', [
    m('.card-header.fw-semibold.d-flex.justify-content-between.align-items-center', [
      m('span', this.getText('LT00000341', 'Scope & filters')),
      m('.d-flex.gap-2', [
        m('button.btn.btn-sm.btn-outline-secondary', {
          disabled: !scopeIsComplete(),
          onclick: () => { onExport('APPROVAL'); },
        }, this.getText('LT00000342', 'Export APPROVAL')),
        m('button.btn.btn-sm.btn-outline-secondary', {
          disabled: !scopeIsComplete(),
          onclick: () => { onExport('PAYROLL'); },
        }, this.getText('LT00000343', 'Export PAYROLL')),
      ]),
    ]),
    m('.card-body', m('.row.g-4', [
      m('.col-md-2.position-relative', [
        m('label.form-label[for=rpProductGroup]', this.getText('LT00000008', 'Product Group')),
        m(SingleSelectComponent, {
          id: 'rpProductGroup',
          items: PRODUCT_GROUPS,
          selectedItem: scope.productGroupCode,
          checkmark: true,
          keyOnly: true,
          onSelect: (item) => { scope.productGroupCode = item; },
        }),
      ]),
      m('.col-md-2.position-relative', [
        m('label.form-label[for=rpPeriodType]', this.getText('LT00000264', 'Period Type')),
        m(SingleSelectComponent, {
          id: 'rpPeriodType',
          items: PERIOD_TYPES,
          selectedItem: scope.periodType,
          checkmark: true,
          keyOnly: true,
          onSelect: (item) => { scope.periodType = item; },
        }),
      ]),
      m('.col-md-2', [
        m('label.form-label', this.getText('LT00000265', 'Period Code')),
        m('input.form-control.form-control-bg', {
          value: scope.periodCode,
          placeholder: '202603 / W10',
          oninput: (e) => { scope.periodCode = e.target.value; },
        }),
      ]),
      m('.col-md-2', [
        m('label.form-label', this.getText('LT00000344', 'Run (optional)')),
        /* Blank = let the server resolve it, which prefers a payout run over a nightly one. */
        m('input.form-control.form-control-bg', {
          value: scope.calcRunId,
          placeholder: this.getText('LT00000345', 'latest'),
          oninput: (e) => { scope.calcRunId = e.target.value; },
        }),
      ]),
      m('.col-md-2', [
        m('label.form-label', this.getText('LT00000299', 'Position')),
        m('input.form-control.form-control-bg', {
          value: positionCode,
          placeholder: 'EC REGULAR',
          oninput: (e) => { positionCode = e.target.value; },
        }),
      ]),
      m('.col-md-2', [
        m('label.form-label', this.getText('LT00000306', 'Site')),
        m('input.form-control.form-control-bg', {
          value: siteCode,
          oninput: (e) => { siteCode = e.target.value; },
        }),
      ]),
      m('.col-md-3.position-relative', [
        m('label.form-label[for=rpPaidOnly]', this.getText('LT00000346', 'Show')),
        m(SingleSelectComponent, {
          id: 'rpPaidOnly',
          items: PAID_FILTERS,
          selectedItem: paidOnly,
          checkmark: true,
          keyOnly: true,
          onSelect: (item) => { paidOnly = item; },
        }),
      ]),
      m('.col-md-2.d-flex.align-items-end',
        m('button.btn.btn-outline-primary.w-100', {
          disabled: !scopeIsComplete(),
          onclick: loadActiveTab,
        }, this.getText('LT00000021', 'Search'))),
    ])),
  ]);

  const summaryTab = () => {
    if (!summary) {
      return m('.text-muted', this.getText('LT00000347', 'Search a scope to see figures.'));
    }
    if (summary.refusal) {
      return m('.alert.alert-warning.mb-0', summary.refusal);
    }

    const positionTable = summary.positions.length === 0
      ? m('.text-muted.small', this.getText('LT00000348', 'No positions in this run.'))
      : m('.table-responsive', m('table.table.table-sm', [
          m('thead', m('tr', [
            m('th', this.getText('LT00000299', 'Position')),
            m('th.text-end', this.getText('LT00000294', 'Employees')),
            m('th.text-end', this.getText('LT00000300', 'Capped')),
            m('th.text-end', this.getText('LT00000301', 'Disqualified')),
            m('th.text-end', this.getText('LT00000302', 'Zero pay')),
            m('th.text-end', this.getText('LT00000296', 'Total')),
          ])),
          m('tbody', summary.positions.map(row => m('tr', [
            m('td', row.positionCode),
            m('td.text-end', num(row.employees)),
            m('td.text-end', num(row.cappedCount)),
            m('td.text-end', num(row.disqualifiedCount)),
            m('td.text-end', num(row.zeroPayCount)),
            m('td.text-end', num(row.totalFinalAmount, 2)),
          ]))),
        ]));

    /* A period where hundreds earned nothing is either a bad month or a broken mapping, and the
       reason codes are the only thing that distinguishes them. The procedure returns a
       plain-words interpretation per code - show it rather than the code alone. */
    const zeroTable = summary.zeroReasons.length === 0
      ? null
      : m('div', [
          m('div.fw-semibold.mb-2.mt-4', this.getText('LT00000349', 'Why people earned nothing')),
          m('.table-responsive', m('table.table.table-sm', [
            m('thead', m('tr', [
              m('th', this.getText('LT00000308', 'Reason')),
              m('th.text-end', this.getText('LT00000350', 'People')),
              m('th', this.getText('LT00000351', 'What it means')),
            ])),
            m('tbody', summary.zeroReasons.map(row => m('tr', [
              m('td.small', row.disqualReasonCode || m('em.text-danger', '(none)')),
              m('td.text-end', num(row.people || row.personCount)),
              m('td.small.text-muted', row.interpretation),
            ]))),
          ])),
        ]);

    return m('div', [positionTable, zeroTable]);
  };

  const employeeTab = () => {
    if (!employees.length) {
      return m('.text-muted', this.getText('LT00000352',
        'No employees for this scope and filter.'));
    }
    return m('.table-responsive', m('table.table.table-sm.table-hover', [
      m('thead', m('tr', [
        m('th', this.getText('LT00000305', 'Employee')),
        m('th', this.getText('LT00000353', 'Name')),
        m('th', this.getText('LT00000299', 'Position')),
        m('th', this.getText('LT00000306', 'Site')),
        m('th', this.getText('LT00000354', 'Grade')),
        m('th.text-end', this.getText('LT00000355', 'Sellout')),
        m('th.text-end', this.getText('LT00000273', 'AR')),
        m('th.text-end', this.getText('LT00000356', 'Regular')),
        m('th.text-end', this.getText('LT00000307', 'Final')),
        m('th', this.getText('LT00000357', 'Cap / Reason')),
        m('th', ''),
      ])),
      m('tbody', employees.map(row => m('tr', {
        class: breakdownUserId === (row.userID || row.userId) ? 'table-active' : '',
      }, [
        m('td', row.userID || row.userId),
        m('td.small', row.userName),
        m('td.small', row.positionCode),
        m('td.small', row.siteCode),
        m('td.small', row.performanceGrade),
        m('td.text-end', num(row.eulaValidatedAmt, 2)),
        m('td.text-end', pct(row.achievementRateAmt)),
        m('td.text-end', num(row.incentiveRegular, 2)),
        m('td.text-end.fw-semibold', num(row.finalIncentiveAmt, 2)),
        m('td.small',
          row.isDisqualified
            ? m('span.badge.bg-danger', row.disqualReasonCode || 'DISQUALIFIED')
            : row.isCapped
              ? m('span.badge.bg-warning.text-dark', row.capReasonCode || 'CAPPED')
              : (row.disqualReasonCode
                  ? m('span.badge.bg-light.text-dark', row.disqualReasonCode)
                  : '')),
        m('td.text-end',
          m('button.btn.btn-sm.btn-outline-primary', {
            onclick: () => { loadBreakdown(row.userID || row.userId); },
          }, this.getText('LT00000358', 'Why?'))),
      ]))),
    ]));
  };

  const storeTab = () => {
    if (!stores.length) {
      return m('.text-muted', this.getText('LT00000359', 'No stores for this scope.'));
    }
    return m('div', [
      m('.alert.alert-info.small', this.getText('EM00000294',
        'Target and achievement are blank here. No calculator writes store-level results yet - '
      + 'only the EC pipeline exists, and store results belong to the leader and manager '
      + 'pipelines that do not - so this is a derived roll-up of what was PAID. It cannot show '
      + 'what the store was aiming at.')),
      m('.table-responsive', m('table.table.table-sm', [
        m('thead', m('tr', [
          m('th', this.getText('LT00000306', 'Site')),
          m('th', this.getText('LT00000360', 'Site Name')),
          m('th.text-end', this.getText('LT00000294', 'Employees')),
          m('th.text-end', this.getText('LT00000355', 'Sellout')),
          m('th.text-end', this.getText('LT00000361', 'Target')),
          m('th.text-end', this.getText('LT00000273', 'AR')),
          m('th.text-end', this.getText('LT00000296', 'Total')),
        ])),
        m('tbody', stores.map(row => m('tr', [
          m('td', row.siteCode),
          m('td.small', row.siteName),
          m('td.text-end', num(row.employees)),
          m('td.text-end', num(row.selloutAmt, 2)),
          m('td.text-end', nullable(row.targetAmt, 2)),
          m('td.text-end', row.achievementRateAmt === null
              || row.achievementRateAmt === undefined
              ? m('span.text-muted', '-') : pct(row.achievementRateAmt)),
          m('td.text-end', num(row.totalFinalAmount, 2)),
        ]))),
      ])),
    ]);
  };

  const breakdownSection = () => {
    if (!breakdown) {
      return null;
    }
    const e = breakdown.employee;

    return m('.card.mb-3', [
      m('.card-header.fw-semibold',
        this.getText('LT00000362', 'Why this figure') + ' - ' + (breakdownUserId || '')),
      m('.card-body', [
        e && m('.row.g-3.mb-3', [
          m('.col-md-3.small', [m('div.text-muted', this.getText('LT00000353', 'Name')),
            m('div', e.userName || '-')]),
          m('.col-md-3.small', [m('div.text-muted', this.getText('LT00000299', 'Position')),
            m('div', e.positionCode || '-')]),
          m('.col-md-3.small', [m('div.text-muted', this.getText('LT00000354', 'Grade')),
            m('div', e.performanceGrade || '-')]),
          m('.col-md-3.small', [m('div.text-muted', this.getText('LT00000307', 'Final')),
            m('div.fw-semibold', num(e.finalIncentiveAmt, 2) + ' ' + (e.currency || ''))]),
        ]),

        breakdown.components.length === 0
          ? m('.text-muted.small', this.getText('LT00000363',
              'No components - this person produced no calculated line at all.'))
          : m('.table-responsive', m('table.table.table-sm', [
              m('thead', m('tr', [
                m('th', this.getText('LT00000364', 'Component')),
                m('th', this.getText('LT00000265', 'Period')),
                m('th', this.getText('LT00000270', 'Segment')),
                m('th.text-end', this.getText('LT00000365', 'Input')),
                m('th.text-end', this.getText('LT00000361', 'Target')),
                m('th.text-end', this.getText('LT00000273', 'AR')),
                m('th.text-end', this.getText('LT00000366', 'Rate')),
                m('th.text-end', this.getText('LT00000367', 'Multiplier')),
                m('th.text-end', this.getText('LT00000307', 'Amount')),
                m('th', this.getText('LT00000368', 'Derivation')),
              ])),
              m('tbody', breakdown.components.map(row => m('tr', [
                m('td.small', row.componentCode),
                m('td.small', row.periodCode),
                m('td.small',
                  (row.segment1 || '?') + ' / ' + (row.segment2 || '?')),
                m('td.text-end', num(row.inputAmount, 2)),
                m('td.text-end', nullable(row.targetAmount, 2)),
                m('td.text-end', pct(row.achievementRate)),
                m('td.text-end', row.appliedRate === null || row.appliedRate === undefined
                    ? '-' : (Number(row.appliedRate) * 100).toFixed(2) + '%'),
                m('td.text-end', row.appliedMultiplier),
                m('td.text-end', num(row.componentAmount, 2)),
                /* parameterTrace is the whole reason this table is worth opening. */
                m('td.small.text-muted', row.parameterTrace),
              ]))),
            ])),

        m('.d-flex.justify-content-end.mt-2',
          m('button.btn.btn-sm.btn-outline-secondary', {
            onclick: () => { breakdown = null; breakdownUserId = null; },
          }, this.getText('LT00000369', 'Close'))),
      ]),
    ]);
  };

  const tabBody = () => {
    switch (activeTab) {
      case TABS.employee: return employeeTab();
      case TABS.store: return storeTab();
      default: return summaryTab();
    }
  };

  const TAB_LABELS = [
    [TABS.summary, 'LT00000370', 'Summary'],
    [TABS.employee, 'LT00000371', 'Employee'],
    [TABS.store, 'LT00000372', 'Store'],
  ];

  /* ---- view -------------------------------------------------------------- */

  return {
    oninit: () => {
      /* Nothing loads until a period is given. */
    },

    view: () => {
      return [
        filterSection(),
        authorityBanner(),
        runLine(),

        m('.card.mb-3', [
          m('.card-header.pb-0',
            m('ul.nav.nav-tabs.card-header-tabs',
              TAB_LABELS.map(([tab, code, fallback]) => m('li.nav-item',
                m('a.nav-link.cursor-pointer', {
                  class: activeTab === tab ? 'active' : '',
                  onclick: () => {
                    activeTab = tab;
                    if (scopeIsComplete()) { loadActiveTab(); }
                  },
                }, this.getText(code, fallback)))))),
          m('.card-body', tabBody()),
        ]),

        breakdownSection(),
        this.getBaseViews(),
      ];
    },
  };
}
